import courseService from 'ApiService/Admin/courseService';
import studentService from 'ApiService/Admin/studentService';
import teacherclassService from 'ApiService/Teacher/Class';
import batchService from 'ApiService/Admin/batchService';

import Card from 'components/card';
import { AuthContext } from 'contexts/AuthContext';
import { useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { UseContextProvider } from 'views/admin/main/users/newUser/contexts/StepperContext';

import BatchInfo from './BatchInfo/Index';
import SelectClass from './Class/Index';

const Index = () => {
  const [currentStep, setCurrentStep] = useState(1);

  const [classData, setClassData] = useState({
    courseId: '',
    batchId: '',
  });

  const [attendance, setAttendance] = useState([]);
  const [disable, setDisable] = useState(true);
  const [loading, setLoading] = useState(false);

  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(true);

  // ✅ NEW: Batch structure state
  const [batchStructures, setBatchStructures] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(false);

  const { state } = useContext(AuthContext);
  const user = state?.user;
  const role = user?.role;
  const isSuperAdmin = role === 'superadmin';

  const selectedBranch = isSuperAdmin
    ? state?.selectedBranch
    : user?.branch?._id;

  const steps = [
    { stepNo: 1, name: 'Class Info' },
    { stepNo: 2, name: 'Add Details' },
  ];

  // =============================
  // Fetch Courses
  // =============================
  const fetchCourses = async () => {
    setCourses([]);
    setLoadingCourses(true);

    try {
      let response;

      if (state.user.role === 'teacher') {
        response = await teacherclassService.getCourses();
      } else if (
        state.user.role === 'admin' ||
        state.user.role === 'superadmin'
      ) {
        response = await courseService.getAllCourses(selectedBranch);
      }

      if (response?.data?.success) {
        setCourses(response.data.courses || []);
      } else {
        toast.error('Failed to fetch courses.');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to fetch courses.');
    } finally {
      setLoadingCourses(false);
    }
  };

  useEffect(() => {
    fetchCourses();
  }, [selectedBranch]);

  // =============================
  // ✅ Fetch Batch Structures
  // =============================
  const fetchBatchStructures = async (courseId) => {
    if (!courseId || !selectedBranch) return;

    setLoadingBatches(true);
    setBatchStructures([]);

    try {
      const res = await batchService.getBatchStructuresByCourse(
        courseId,
        selectedBranch
      );

      setBatchStructures(res?.data?.batchStructures || []);
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Failed to fetch batch structures.'
      );
    } finally {
      setLoadingBatches(false);
    }
  };

  // Auto fetch batches when course changes
  useEffect(() => {
    if (classData.courseId) {
      fetchBatchStructures(classData.courseId);

      // reset batch when course changes
      setClassData((prev) => ({ ...prev, batchId: '' }));
    } else {
      setBatchStructures([]);
    }
  }, [classData.courseId, selectedBranch]);

  const clearAttendance = () => {
    setAttendance([]);
  };

  // =============================
  // Step Renderer
  // =============================
  const displayStep = (step) => {
    switch (step.stepNo) {
      case 1:
        return (
          <SelectClass
            setLoading={setLoading}
            setDisable={setDisable}
            setClassData={setClassData}
            classData={classData}
            courses={courses}
            loadingCourses={loadingCourses}
            clearAttendance={clearAttendance}
            batchStructures={batchStructures}
          />
        );

      case 2:
        return (
          <BatchInfo
            setLoading={setLoading}
            setDisable={setDisable}
            classData={classData}
            setClassData={setClassData}
            selectedBranch={selectedBranch}
            batchStructures={batchStructures}
            loadingBatches={loadingBatches}
          />
        );

      default:
        return null;
    }
  };

  // =============================
  // Submit
  // =============================
  async function handleSubmit() {
    if (attendance.length === 0) {
      toast.error('No students found.');
      return;
    }

    try {
      const studentsWithPriority = attendance
        .filter((student) => student.ready === true)
        .map((student) => ({
          studentId: student.id,
          placementPriority: student.placementPriority,
        }));

      const payload = {
        studentIds: studentsWithPriority,
        courseId: classData.courseId,
        batchId: classData.batchId,
        selectedBranch: state.selectedBranch,
      };

      const response = await studentService.makeReadyForPlacement(payload);

      if (response.data.success) {
        toast.success(response.data.message);
        setCurrentStep(1);
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          'An error occurred while submitting attendance.'
      );
    }
  }

  // =============================
  // Navigation
  // =============================
  const handleNext = () => {
    if (disable) return;
    setLoading(true);
    setCurrentStep((prev) => prev + 1);
    setLoading(false);
  };

  const handleBack = () => {
    if (disable) return;
    setCurrentStep((prev) => prev - 1);
  };

  const handleKeyDown = (event) => {
    if (disable) return;

    if (event.key === 'Enter' && currentStep === 1) {
      handleNext();
    } else if (event.key === 'ArrowLeft' && currentStep > 1) {
      handleBack();
    } else if (event.key === 'ArrowRight' && currentStep < steps.length) {
      handleNext();
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentStep, disable]);

  // =============================
  // UI
  // =============================
  return (
    <div className="mt-10 h-full w-full md:mt-10">
      <div className="h-[350px] w-full rounded-[20px] bg-gradient-to-br from-brand-400 to-brand-600 md:h-[390px]" />

      <div
        className={`mx-auto h-full ${
          currentStep === 1 ? 'w-5/6' : 'w-6/6'
        } md:px-3`}
      >
        <Card extra="-mt-[280px] w-full md:-mt-[340px] md:px-[70px] h-full mx-auto pb-3">
          <UseContextProvider>
            {displayStep(steps[currentStep - 1])}
          </UseContextProvider>

          {currentStep === 1 ? (
            <div className="flex justify-end px-3 pt-2 md:px-8">
              <button
                disabled={disable}
                onClick={handleNext}
                className="rounded-xl bg-brand-900 px-16 py-2 font-medium text-white hover:bg-brand-800"
              >
                {loading ? 'Loading...' : 'Next'}
              </button>
            </div>
          ) : (
            <div className="flex justify-between px-3 pt-2 md:px-8">
              <button
                onClick={handleBack}
                className="rounded-xl bg-lightPrimary px-16 py-2 font-medium text-navy-700"
              >
                Prev
              </button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default Index;
